import React from "react";
import { ShieldAlert, Users, Timer, ChevronRight } from "lucide-react";
import { LOS_BANDS, COMMAND_TIERS } from "../services/crowdSafety";

/* Crowd safety — the density view.
 *
 * Leads with the command tier and the worst density, because those are the
 * two things an incident commander needs in the first two seconds. Density
 * is shown as BOTH persons/m² and m²/person: the first is the standard
 * safety figure, the second is the one people can picture — "0.27 m² each"
 * communicates a crush far better than "3.7 persons per square metre".
 */
export default function CrowdSafetyPanel({ lang = "en", picture, onFocus }) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";
  const { sectors, commandTier, worst, soonest, assumptions } = picture;
  const tier = COMMAND_TIERS[commandTier];

  return (
    <div className="border-t border-slate-200">
      <section className="px-3 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <ShieldAlert size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "गर्दी सुरक्षा" : "Crowd Safety"}
          </span>
          <span className="ml-auto text-[8px] font-black px-1.5 py-0.5 rounded border bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300">
            SIM
          </span>
        </div>

        {/* Command tier — who should be acting, not just what is happening. */}
        <div
          className="rounded-lg border-2 p-2.5 mb-2"
          style={{ borderColor: tier.color, background: `${tier.color}14` }}
        >
          <div className="text-[9px] font-black uppercase tracking-wider" style={{ color: tier.color }}>
            {lang === "mr" ? "कमांड स्तर" : "Command tier"}
          </div>
          <div className="text-[14px] font-black mt-0.5" style={{ color: tier.color }}>
            {L(tier.label)}
          </div>
          {worst && (
            <div className="text-[10px] text-slate-700 mt-1">
              {lang === "mr" ? "सर्वाधिक घनता" : "Peak density"}:{" "}
              <span className="font-black tabular-nums">{worst.density} /m²</span>{" "}
              — {L(worst.name)} ({worst.band.id})
            </div>
          )}
        </div>

        {/* Soonest band escalation — the tightest clock in the theatre. */}
        {soonest?.nextBand && (
          <button
            type="button"
            onClick={() => onFocus({ coord: soonest.centre, zoom: 15 })}
            className="w-full text-left rounded-lg border border-orange-300 bg-orange-50 p-2 mb-2 hover:border-orange-400"
          >
            <div className="flex items-center gap-1.5">
              <Timer size={11} className="text-orange-700" />
              <span className="text-[9px] font-black uppercase tracking-wider text-orange-700">
                {lang === "mr" ? "पुढील स्तरापर्यंत" : "Next band in"}
              </span>
            </div>
            <div className="text-[13px] font-black text-orange-900 tabular-nums mt-0.5">
              {soonest.minutesToNextBand} {lang === "mr" ? "मिनिटे" : "min"}
              <span className="text-[10px] font-bold ml-1">
                → {soonest.nextBand.id} · {L(soonest.nextBand.label)}
              </span>
            </div>
            <div className="text-[10px] text-orange-800">{L(soonest.name)}</div>
          </button>
        )}

        {/* Per-sector density, worst first. */}
        <div className="space-y-1">
          {sectors.map((s) => (
            <button
              key={s.sectorId}
              type="button"
              onClick={() => onFocus({ coord: s.centre, zoom: 15 })}
              className="w-full text-left rounded-lg border p-2 transition-colors hover:brightness-95"
              style={{ borderColor: `${s.band.color}66`, background: `${s.band.color}12` }}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="flex h-4 w-4 items-center justify-center rounded text-[9px] font-black text-white flex-shrink-0"
                  style={{ background: s.band.color }}
                >
                  {s.band.id}
                </span>
                <span className="text-[11px] font-bold text-slate-800 truncate flex-1">
                  {L(s.name)}
                </span>
                <span className="text-[11px] font-black tabular-nums" style={{ color: s.band.color }}>
                  {s.density}
                </span>
                <span className="text-[8px] text-slate-400">/m²</span>
              </div>

              {/* Both framings: the standard figure and the human one. */}
              <div className="mt-0.5 text-[9px] text-slate-600 tabular-nums">
                {s.areaPerPerson} m² {lang === "mr" ? "प्रति व्यक्ती" : "each"}
                {" · "}{s.people.toLocaleString("en-IN")}{" "}
                {lang === "mr" ? "व्यक्ती" : "people"}
                {" · "}{(s.areaM2 / 1000).toFixed(0)}k m²
                {s.netPerMin !== 0 && (
                  <span className={s.netPerMin > 0 ? " text-orange-700 font-bold" : " text-emerald-700"}>
                    {" · "}{s.netPerMin > 0 ? "+" : ""}{s.netPerMin}/min
                  </span>
                )}
              </div>

              <div className="mt-0.5 text-[9px] font-semibold text-slate-700 leading-snug">
                {L(s.band.action)}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Band reference — a level that does not say what changes at that
          level is just a colour. */}
      <section className="px-3 pb-3">
        <details>
          <summary className="text-[9px] font-bold text-slate-400 cursor-pointer hover:text-slate-600 flex items-center gap-0.5">
            <ChevronRight size={8} />
            {lang === "mr" ? "स्तर संदर्भ व गृहीतके" : "Band reference & assumptions"}
          </summary>

          <table className="mt-1.5 w-full text-[9px]">
            <tbody>
              {LOS_BANDS.map((b, i) => (
                <tr key={b.id}>
                  <td className="py-0.5 pr-1 align-top">
                    <span
                      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded text-[8px] font-black text-white"
                      style={{ background: b.color }}
                    >
                      {b.id}
                    </span>
                  </td>
                  <td className="py-0.5 pr-1 align-top tabular-nums text-slate-500 whitespace-nowrap">
                    {i === 0 ? "< " : `${LOS_BANDS[i - 1].maxDensity}–`}
                    {Number.isFinite(b.maxDensity) ? b.maxDensity : "∞"}
                  </td>
                  <td className="py-0.5 align-top text-slate-600 leading-snug">
                    <span className="font-bold text-slate-700">{L(b.label)}</span> — {L(b.meaning)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <ul className="mt-1.5 space-y-0.5">
            {assumptions.map((a) => (
              <li key={a} className="text-[9px] leading-snug text-slate-500">· {a}</li>
            ))}
          </ul>
        </details>
      </section>
    </div>
  );
}
